// TrailLine — the thread's quiet one-liner (pen "Chat – every entry", the
// "Spår" row): a status glyph, one line of plain words, then either a fold
// chevron (details one click away) or one text action (Continue). Used by tool
// activity, merged trail lines, the stopped notice and the unknown-entry
// default, so every trail entry reads the same.
//
// Pure apart from `open`, which is view-ephemeral (survives no reload,
// carries no domain meaning). Folded content is passed as `children`; with no
// children there is nothing to open and no chevron.

import { exhaustSwitchCase } from "@bilda/utils";
import { type ReactNode, useState } from "react";
import { Icon, type IconName } from "../../Icon/Icon.js";
import "./TrailLine.css";

export type TrailGlyph =
    | "done"
    | "running"
    | "failed"
    | "stopped"
    | "unknown"
    | "answered"
    | "skipped"
    | "discarded"
    // What the assistant changed because of messages sent while it worked
    // (the Activity entry pattern; PS-61).
    | "changed";

const glyphIcon = (glyph: TrailGlyph): IconName => {
    switch (glyph) {
        case "done":
            return "check";
        case "running":
            return "loading";
        case "failed":
            return "warning";
        case "stopped":
            return "stop";
        case "unknown":
            return "help";
        case "answered":
            return "answered";
        case "skipped":
            return "skipped";
        case "discarded":
            return "close";
        case "changed":
            return "sparkle";
        default:
            return exhaustSwitchCase(glyph);
    }
};

export type TrailAction = {
    readonly label: string;
    readonly onAction: () => void;
};

export const TrailLine = ({
    glyph,
    text,
    action,
    children,
}: {
    readonly glyph: TrailGlyph;
    readonly text: string;
    readonly action?: TrailAction;
    readonly children?: ReactNode;
}): ReactNode => {
    const [open, setOpen] = useState(false);
    const foldable = children !== undefined && children !== null;
    const head = (
        <>
            <Icon
                name={glyphIcon(glyph)}
                className={`bilda-trail__glyph bilda-trail__glyph--${glyph}`}
            />
            <span className="bilda-trail__text" title={text}>
                {text}
            </span>
        </>
    );
    return (
        <div className={`bilda-trail bilda-trail--${glyph}`}>
            {foldable ? (
                <button
                    type="button"
                    className="bilda-trail__row bilda-trail__row--button"
                    aria-expanded={open}
                    onClick={() => setOpen((shown) => !shown)}
                >
                    {head}
                    <Icon
                        name={open ? "chevron-up" : "chevron-down"}
                        className="bilda-trail__chevron"
                    />
                </button>
            ) : (
                <div className="bilda-trail__row">
                    {head}
                    {action === undefined ? null : (
                        <button
                            type="button"
                            className="bilda-trail__action"
                            onClick={action.onAction}
                        >
                            {action.label}
                        </button>
                    )}
                </div>
            )}
            {foldable && open ? (
                <div className="bilda-trail__steps">{children}</div>
            ) : null}
        </div>
    );
};
